/**
 * Manifest delle route indicizzabili: unica sorgente per sitemap, prerender e gate SEO.
 * Non importa React né asset: viene serializzato in `window.__SEO_ROUTES__` e letto
 * dagli script Node durante il build.
 */

import { GUIDES, ROUNDUPS } from "@/data/content";

/**
 * @typedef {Object} SeoRoute
 * @property {string} path      URL relativo con slash iniziale, senza trailing slash (root = "/")
 * @property {number} priority  0.1–1.0 per la sitemap
 * @property {string|null} lastmod ISO date, o null se non esiste una data reale
 */

/** Priorità sitemap per tipo di pagina. */
const PRIORITY = {
  home: 1.0,
  sectionIndex: 0.8,
  page: 0.7,
  legal: 0.3,
};

/**
 * Route statiche del router, escluse la catch-all `*` e gli URL con query string
 * (es. `/gadget?cat=…`, che è una vista filtrata della stessa pagina).
 */
const STATIC_ROUTES = [
  { path: "/", priority: PRIORITY.home },
  { path: "/guide", priority: PRIORITY.sectionIndex },
  { path: "/gadget", priority: PRIORITY.sectionIndex },
  { path: "/metodo", priority: PRIORITY.page },
  { path: "/chi-sono", priority: PRIORITY.page },
  { path: "/disclosure", priority: PRIORITY.legal },
  { path: "/privacy", priority: PRIORITY.legal },
  { path: "/cookie", priority: PRIORITY.legal },
  { path: "/termini", priority: PRIORITY.legal },
  { path: "/note-legali", priority: PRIORITY.legal },
];

/**
 * @returns {SeoRoute[]} Route indicizzabili in ordine deterministico e senza duplicati.
 */
export function getSeoRoutes() {
  const entries = [
    ...STATIC_ROUTES.map((route) => ({ ...route, lastmod: null })),
    ...GUIDES.map((guide) => ({
      path: `/guida/${guide.slug}`,
      priority: PRIORITY.page,
      lastmod: guide.updatedAt || guide.publishedAt || null,
    })),
    ...ROUNDUPS.map((roundup) => ({
      path: `/gadget/${roundup.slug}`,
      priority: PRIORITY.page,
      lastmod: roundup.updatedAt || roundup.publishedAt || null,
    })),
  ];

  const byPath = new Map();
  for (const entry of entries) {
    if (!byPath.has(entry.path)) byPath.set(entry.path, entry);
  }
  return Array.from(byPath.values());
}
